import { InitiativeUniversalTracker } from '@g17eco/types/initiativeUniversalTracker';
import { BulkActionUtr } from '@g17eco/types/survey-question-list';
import { UtrValueType } from '@g17eco/types/universalTracker';
import { PopoverButton } from '@g17eco/molecules/popover';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import {
  hasOverriddenUtrvConfig,
  hasOverriddenVariations,
  iconMap,
  UtrvConfigCode,
  UtrvConfigValue,
} from '@features/question-configuration';

const TITLES = {
  unitInput: 'Unit',
  numberScaleInput: 'Number scale',
  decimal: 'Decimal',
};

const getTableInputOverrides = ({
  utr,
  initiativeUtr,
  prop,
}: {
  utr: BulkActionUtr;
  initiativeUtr: InitiativeUniversalTracker | undefined;
  prop: 'unitInput' | 'numberScaleInput' | 'decimal';
}) => {
  const columns = utr.valueValidation?.table?.columns;
  return columns ? (
    <PopoverButton id={`${utr._id}-${prop}`} title={TITLES[prop]}>
      {columns.map((col) => {
        const initiativeUtrColumn = initiativeUtr?.valueValidation?.table?.columns.find((c) => c.code === col.code);
        const value = prop === 'decimal' ? initiativeUtrColumn?.validation?.decimal : initiativeUtrColumn?.[prop];
        return (
          <div key={col.code} className='d-flex align-items-center justify-content-between gap-3 py-1'>
            <div>{col.name}</div>
            <div>{value ?? '-'}</div>
          </div>
        );
      })}
    </PopoverButton>
  ) : (
    '-'
  );
};

export const getDisplayedInputOverrides = (
  utr: BulkActionUtr,
  rootInitiativeUtrMap: Map<string, InitiativeUniversalTracker>
) => {
  switch (utr.valueType) {
    case UtrValueType.Percentage:
    case UtrValueType.NumericValueList:
    case UtrValueType.Number: {
      const initiativeUtr = rootInitiativeUtrMap.get(utr._id);
      const unitInput = initiativeUtr?.unitInput ?? '-';
      const numberScaleInput = initiativeUtr?.numberScaleInput ?? '-';
      const decimal = initiativeUtr?.valueValidation?.decimal ?? '-';

      return {
        unit: <span>{unitInput}</span>,
        numberScale: <span>{numberScaleInput}</span>,
        decimal: <span>{decimal}</span>,
      };
    }
    case UtrValueType.Table: {
      const initiativeUtr = rootInitiativeUtrMap.get(utr._id);
      return {
        unit: getTableInputOverrides({ utr, initiativeUtr, prop: 'unitInput' }),
        numberScale: getTableInputOverrides({ utr, initiativeUtr, prop: 'numberScaleInput' }),
        decimal: getTableInputOverrides({ utr, initiativeUtr, prop: 'decimal' }),
      };
    }
    default:
      return {
        unit: '-',
        numberScale: '-',
        decimal: '-',
      };
  }
};

const getIcon = ({ initiativeUtr, field }: { initiativeUtr: InitiativeUniversalTracker; field: UtrvConfigCode }) => {
  if (!hasOverriddenUtrvConfig({ initiativeUtr, field })) {
    return null;
  }
  const fieldValue = initiativeUtr.utrvConfig?.[field];
  if (fieldValue === UtrvConfigValue.Default) {
    return null;
  }
  const isRequired = fieldValue === UtrvConfigValue.Required;
  return (
    <SimpleTooltip text={`${iconMap[field].label} ${isRequired ? 'required' : 'optional'}`}>
      <i
        className={`${isRequired ? iconMap[field].requiredIcon : iconMap[field].optionalIcon} text-ThemeIconSecondary`}
      ></i>
    </SimpleTooltip>
  );
};

const getVariationIcon = (initiativeUtr: InitiativeUniversalTracker) => {
  if (!hasOverriddenVariations(initiativeUtr)) {
    return null;
  }
  return (
    <SimpleTooltip text={'Variation detection'}>
      <i className={'fal fa-chart-user text-ThemeIconSecondary'}></i>
    </SimpleTooltip>
  );
};

export const getDisplayedOverrides = (
  utr: BulkActionUtr,
  rootInitiativeUtrMap: Map<string, InitiativeUniversalTracker>
) => {
  const initiativeUtr = rootInitiativeUtrMap.get(utr._id);
  if (!initiativeUtr) {
    return '-';
  }
  return (
    <div className='d-flex align-items-center gap-2'>
      {getIcon({ initiativeUtr, field: 'verificationRequired' })}
      {getIcon({ initiativeUtr, field: 'noteRequired' })}
      {getIcon({ initiativeUtr, field: 'isPrivate' })}
      {getIcon({ initiativeUtr, field: 'evidenceRequired' })}
      {getVariationIcon(initiativeUtr)}
    </div>
  );
};
